import { lazy, Suspense, useState } from 'react'

import PencilAltIcon from '@heroicons/react/outline/esm/PencilAltIcon.js'
import { clsx } from 'clsx'
import { Link as RouterLink } from 'react-router-dom'

import { T } from '@tribeplatform/react-components/i18n'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Popover } from '@tribeplatform/react-ui-kit/Popover'

import { generateNewChatPath } from '../../../utils/messaging.utils.js'
import { MessagingDropdownLoading } from '../../Messaging/MessagingDropdownLoading.js'
import { NavbarControlItem } from '../constants.js'
import { MessagingIndicatorBadge } from '../MessagingIndicatorBadge.js'
import { getIcon } from '../utils.js'

export type NavbarControlMessagingProps = {
  item: NavbarControlItem
  className?: string
}

const MessagingDropdownList = lazy(() =>
  import('../../Messaging/MessagingDropdownList.js').then(m => ({
    default: m.MessagingDropdownList,
  })),
)

export const NavbarControlMessaging = ({
  item,
  className,
}: NavbarControlMessagingProps) => {
  const [popoverActive, setPopoverActive] = useState(false)

  const close = () => setPopoverActive(false)

  const Icon = getIcon(item.icon)

  return (
    <Popover
      placement="bottom-end"
      open={popoverActive}
      onClose={close}
      activator={
        <MessagingIndicatorBadge className={clsx('translate-y-0')}>
          <Button
            variant="outline"
            rounded
            onClick={() => setPopoverActive(!popoverActive)}
            trailingIcon={Icon ? <Icon className="h-5 w-5" /> : null}
            aria-label={item.label}
            className={className}
          />
        </MessagingIndicatorBadge>
      }
      fluidContent={false}
      fixed
    >
      <div className="flex flex-col">
        <div className="border-b border-line-subdued p-3 flex align-middle items-center space-s-3">
          <RouterLink
            className="flex-grow text-content-subdued font-bold hover:text-content-hovered"
            to="/messaging"
            onClick={close}
          >
            <T defaultMessage="Messages" id="Generics.Messages" />
          </RouterLink>

          <RouterLink
            to={generateNewChatPath()}
            className="flex-shrink-0"
            onClick={close}
          >
            <PencilAltIcon className="h-5 w-5" />
          </RouterLink>
        </div>
        <div className="grow">
          <Suspense fallback={<MessagingDropdownLoading />}>
            <MessagingDropdownList closeDropdown={close} />
          </Suspense>
        </div>
        <div className="border-t border-line-subdued flex align-middle items-center space-s-3">
          <RouterLink
            className="flex-grow p-3 text-content-subdued text-center text-sm hover:text-content-hovered"
            to="/messaging"
            onClick={close}
          >
            <T defaultMessage="See all messages" id="Generics.SeeAllMessages" />
          </RouterLink>
        </div>
      </div>
    </Popover>
  )
}
